import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Header, Button, Icon, Dropdown, Message } from 'semantic-ui-react';
import DeleteConfirmationModalWrapper from '../common/DeleteConfirmationModalWrapper';
import { v4 as uuidv4 } from 'uuid';
import ManagementHeader from '../../../common/components/ManagementHeader';
import { setVariationSelection } from '../../slices/bookWorksheetDataSlice';
import useEditVersionManager from '../../hooks/useEditVersionManager_upgrade';
import {
  useStableVariationManagement,
  useStableSharedData,
  useStableCharacterZone
} from '../../hooks/useStableHooks';
import { VariationFormPanel } from '../forms';
import { VariationsListEnh } from '../lists';
import { getSignedUrlForS3Path } from '../../../utils/s3PathUtils';
// Removed context imports - using component state instead
import { useSignedUrl } from '../../hooks/useSignedUrl';
// Import the real uploadCompleteVersion from the recovered utilities slice
import { uploadCompleteVersion } from '../../slices/bwdUtilitiesSlice';
import '../../styles/bookWorksheet.css';

/**
 * Enhanced Variation Management component with audio support
 * Manages variations for a selected character
 * 
 * @returns {JSX.Element} VariationManagementEnh component
 */
const VariationManagementEnh = () => {
  const userId = useSelector(state => state.user.profile?.data?.userId);
  const dispatch = useDispatch();

  // Use the stable variation management hook
  const variationManagement = useStableVariationManagement();

  // Use stable shared data and character zone hooks
  const sharedData = useStableSharedData();
  const characterZone = useStableCharacterZone();

  // Extract data from new hook
  const {
    activeVariation,
    characterId,
    actions
  } = variationManagement;

  // Get actual shared data
  const { litGroups, books, selections } = sharedData;
  const { characters } = characterZone;

  // Get current selections
  const selectedLitGroup = litGroups.find(lg => lg.uuid === selections.litGroupId) || null;
  const selectedBook = books.find(b => b.uuid === selections.bookId) || null;
  const selectedCharacter = characters.find(c => c.uuid === selections.characterId) || null;
  const selectedVariation = activeVariation;

  // Map actions from shared data and character zone
  const fetchBooks = sharedData.actions.fetchBooks;
  const fetchCharacters = characterZone.actions.fetchCharacters;
  const fetchVariations = variationManagement.actions.fetchVariations; // Use the zone-based fetch
  const createVariation = actions.createVariation;
  const updateVariation = actions.updateVariation;

  // Wrap setSelection in useCallback to fix ESLint warnings
  const setSelection = useCallback((type, value) => {
    if (type === 'litGroupId') sharedData.actions.setLitGroupSelection(value);
    else if (type === 'bookId') sharedData.actions.setBookSelection(value);
    else if (type === 'characterId') characterZone.actions.selectCharacter(value);
    else if (type === 'variationId') dispatch(setVariationSelection(value));
  }, [sharedData.actions, characterZone.actions, dispatch]);

  // Wrap clearSelection in useCallback to fix ESLint warnings
  const clearSelection = useCallback((type) => {
    if (type === 'variationId') dispatch(setVariationSelection(null));
  }, [dispatch]);

  // ✅ PHASE 1: Add useEditVersionManager_upgrade hook
  const {
    loadVariation,
    deleteVariation
  } = useEditVersionManager();

  // Extract IDs from selections
  const litGroupId = selectedLitGroup?.uuid;
  const bookId = selectedBook?.uuid;
  const variationId = selectedVariation?.uuid;

  // Get relationship data from shared state
  const booksForLitGroup = books; // All books are already filtered by the selected lit group
  const charactersForBook = characters; // All characters are already filtered by the selected book

  // Phase 3: Replace context with local component state
  // UI state (previously from VariationContext)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [, setUploadStatus] = useState({
    loading: false,
    error: null,
    success: false
  });

  // Form state (previously from FormStateContext)
  const [isEditing, setIsEditing] = useState(false);
  const [, setPendingRecording] = useState(null);

  // Local state
  const [variationToDelete, setVariationToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingFormValues, setPendingFormValues] = useState({});

  // "Use as Base" functionality state
  const [selectedSampleForNewVariation, setSelectedSampleForNewVariation] = useState(null);
  const [isCreatingFromSample, setIsCreatingFromSample] = useState(false);

  // Create refs
  const formikRef = useRef();
  const variationListRefreshRef = useRef();
  const newVariationIdRef = useRef(uuidv4());

  // Update editing state when variation ID changes
  useEffect(() => {
    // Only set editing to true when we have a variationId
    // Don't automatically set it to false when variationId is null
    // (let other parts of the code explicitly control when to exit edit mode)
    if (variationId) {
      setIsEditing(true);
    }
  }, [variationId, setIsEditing]);

  // Use the signed URL hook for audio playback
  const {
    signedAudioUrl,
    activeParams,
    updateSignedUrl
  } = useSignedUrl(selectedVariation, userId);

  // Reset upload status when selection changes
  useEffect(() => {
    setUploadStatus({ loading: false, error: null, success: false });
  }, [selectedVariation?.uuid, selectedCharacter?.uuid, selectedBook?.uuid]);

  // Apply pending form values when formikRef becomes available
  useEffect(() => {
    if (formikRef.current && Object.keys(pendingFormValues).length > 0) {
      console.log('Applying pending form values:', pendingFormValues);

      // Apply all pending form values
      Object.entries(pendingFormValues).forEach(([field, value]) => {
        if (field === 'selectionDuration') {
          // Special handling for selectionDuration
          if (formikRef.current.selectionDuration) {
            formikRef.current.selectionDuration.current = value;
          }
        } else {
          // Normal form fields
          formikRef.current.setFieldValue(field, value);
        }
      });

      // Clear pending values
      setPendingFormValues({});
    }
  }, [formikRef, pendingFormValues]);

  // Use a ref to keep track of the last character ID we fetched variations for
  const lastFetchedCharacterId = useRef(null);

  // Fetch variations when characterId changes
  useEffect(() => {
    console.log('🎯 [VARIATION_MGMT] useEffect triggered - characterId:', characterId, 'lastFetched:', lastFetchedCharacterId.current);

    if (characterId && characterId !== lastFetchedCharacterId.current) {
      console.log(`🎯 [VARIATION_MGMT] Character ID changed from ${lastFetchedCharacterId.current} to ${characterId}, fetching variations with force=true`);
      // Update the ref before fetching to prevent infinite loops
      lastFetchedCharacterId.current = characterId;

      // Create an async function inside the useEffect
      const fetchData = async () => {
        try {
          console.log(`🎯 [VARIATION_MGMT] Starting fetchVariations for character ${characterId}`);
          // ✅ FIX: Use correct parameter format with litGroupId and bookId
          const params = {
            litGroupId: sharedData.selections?.litGroupId,
            bookId: sharedData.selections?.bookId,
            characterId,
            forceRefresh: true
          };
          console.log(`🎯 [VARIATION_MGMT] Fetch params:`, params);
          // Use await and unwrap() to properly handle the Promise
          const varList = await fetchVariations(params).unwrap();
          console.log(`🎯 [VARIATION_MGMT] Successfully fetched variations for character ${characterId}`, varList);
        } catch (error) {
          console.error(`🎯 [VARIATION_MGMT] Error fetching variations:`, error);
        }
      };

      // Call the async function
      fetchData();
    } else if (characterId === lastFetchedCharacterId.current) {
      console.log(`🎯 [VARIATION_MGMT] Character ID unchanged (${characterId}), skipping fetch`);
    } else if (!characterId) {
      console.log(`🎯 [VARIATION_MGMT] No character ID provided, skipping fetch`);
    }
  }, [characterId, sharedData.selections?.litGroupId, sharedData.selections?.bookId, fetchVariations]);

  // Memoize dropdown options to prevent unnecessary re-renders
  const litGroupOptions = useMemo(() => {
    return selectedLitGroup ? [{
      key: selectedLitGroup.uuid,
      text: selectedLitGroup.name || 'Selected Literary Group',
      value: selectedLitGroup.uuid
    }] : [];
  }, [selectedLitGroup]);

  // Create dropdown options from all available books
  const bookOptions = useMemo(() => {
    let options = (booksForLitGroup || []).filter(book => book && book.uuid).map(book => ({
      key: book.uuid,
      text: book.name || 'Unnamed Book',
      value: book.uuid
    }));

    // If we have a selected book but it's not in the options, add it
    if (selectedBook && !options.some(option => option.value === selectedBook.uuid)) {
      options.unshift({
        key: selectedBook.uuid,
        text: selectedBook.name || 'Selected Book',
        value: selectedBook.uuid
      });
    }

    return options;
  }, [booksForLitGroup, selectedBook]);

  // Create dropdown options from all available characters
  const characterOptions = useMemo(() => {
    let options = (charactersForBook || []).filter(character => character && character.uuid).map(character => ({
      key: character.uuid,
      text: character.name || 'Unnamed Character',
      value: character.uuid
    }));

    // If we have a selected character but it's not in the options, add it
    if (selectedCharacter && !options.some(option => option.value === selectedCharacter.uuid)) {
      options.unshift({
        key: selectedCharacter.uuid,
        text: selectedCharacter.name || 'Selected Character',
        value: selectedCharacter.uuid
      });
    }

    return options;
  }, [charactersForBook, selectedCharacter]);

  // Handle new variation button click
  const handleNewVariationClick = useCallback(() => {
    clearSelection('variationId');
    // Generate a new UUID for the new variation
    newVariationIdRef.current = uuidv4();
    // Explicitly set editing to false for new variations
    setIsEditing(false);
  }, [clearSelection, setIsEditing]);

  // Handle delete variation
  const handleDeleteVariation = useCallback((variation) => {
    setVariationToDelete(variation);
    setShowDeleteModal(true);
  }, [setShowDeleteModal]);

  // Handle confirm delete variation
  const confirmDeleteVariation = useCallback(async () => {
    if (!variationToDelete) return;

    // const deleteTarget = { variationId: variationToDelete.uuid, characterId: characterId };
    // const deleteTarget = { variationId: variationToDelete.uuid, characterId: characterId }

    try {
      // Delete the variation
      // await deleteVariation(deleteTarget);
      await deleteVariation(variationToDelete.uuid);

      // Close the modal
      setShowDeleteModal(false);
      setVariationToDelete(null);

      // If the deleted variation was selected, clear the selection
      if (selectedVariation && selectedVariation.uuid === variationToDelete.uuid) {
        clearSelection('variationId');
      }

      // Refresh the variations list with force=true to bypass cache
      if (characterId) {
        fetchVariations({
          litGroupId: sharedData.selections?.litGroupId,
          bookId: sharedData.selections?.bookId,
          characterId,
          forceRefresh: true
        });
      }

      setSuccessMessage('Variation deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting variation:', error);
      setErrorMessage('Error deleting variation: ' + error.message);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  }, [variationToDelete,
    deleteVariation,
    setShowDeleteModal,
    selectedVariation,
    clearSelection,
    characterId,
    sharedData.selections?.litGroupId, sharedData.selections?.bookId,
    fetchVariations]);

  // Handle lit group selection
  const handleLitGroupSelect = useCallback((value) => {
    setSelection('litGroupId', value);
    clearSelection('variationId');

    // Fetch books for this literary group
    fetchBooks(value).then(() => {
      // After fetching books, get the updated list of books for this LitGroup
      const updatedBooks = booksForLitGroup;

      // If there are books, select the first one with a profile, or the first one if none have profiles
      if (updatedBooks && updatedBooks.length > 0) {
        // Find a book with a profile (not profileMissing)
        const bookWithProfile = updatedBooks.find(book => !book.profileMissing);

        if (bookWithProfile) {
          setSelection('bookId', bookWithProfile.uuid);
        } else {
          setSelection('bookId', updatedBooks[0].uuid);
        }
      }
    });
  }, [setSelection, clearSelection, fetchBooks, booksForLitGroup]);

  // Handle book selection
  const handleBookSelect = useCallback((value) => {
    setSelection('bookId', value);
    clearSelection('variationId');

    // Fetch characters for this book
    fetchCharacters(value);
  }, [setSelection, clearSelection, fetchCharacters]);

  // Handle character selection
  const handleCharacterSelect = useCallback((value) => {
    setSelection('characterId', value);
    clearSelection('variationId');

    // Fetch variations for this character with force=true to bypass cache
    fetchVariations({
      litGroupId: sharedData.selections?.litGroupId,
      bookId: sharedData.selections?.bookId,
      characterId: value,
      forceRefresh: true
    });
  }, [setSelection, clearSelection, fetchVariations, sharedData.selections]);

  // Handle variation selection
  const handleVariationSelect = useCallback(async (variation) => {
    if (variation && variation.uuid) {
      // Set the selection first
      setSelection('variationId', variation.uuid);

      // ✅ PHASE 1: Trigger the upgrade loading workflow
      console.log('[VariationManagement] Loading variation with upgrade workflow:', variation.uuid);

      try {
        await loadVariation(variation.uuid);
        console.log('[VariationManagement] Successfully loaded variation via upgrade hook');
        // Set editing mode to true after successful load
        setIsEditing(true);
      } catch (error) {
        console.error('[VariationManagement] Error loading variation via upgrade hook:', error);
        // Fallback to basic form population if upgrade fails
        if (formikRef.current && variation) {
          formikRef.current.setFieldValue('name', variation.name || '');
          formikRef.current.setFieldValue('content', variation.content || '');
          formikRef.current.setFieldValue('version', variation.version || 1);
          formikRef.current.setFieldValue('priority', variation.priority || 'medium');
          formikRef.current.setFieldValue('status', variation.status || 'not-started');
          formikRef.current.setFieldValue('deadline', variation.deadline || null);
        }

        setIsEditing(true);
      }
    }
  }, [setSelection, setIsEditing, loadVariation]);

  // Handle "Use as Base" functionality
  const handleUseAsBase = useCallback((sample) => {
    console.log('[VariationManagement] Use as Base triggered with sample:', sample);

    // Clear current variation selection
    clearSelection('variationId');

    // Set the sample data for creating new variation
    setSelectedSampleForNewVariation(sample);
    setIsCreatingFromSample(true);

    // Generate a new UUID for the new variation
    newVariationIdRef.current = uuidv4();

    // Set editing mode to false since we're creating a new variation
    setIsEditing(false);
  }, [clearSelection, setIsEditing]);

  // Handle cancel creating from sample
  const handleCancelCreateFromSample = useCallback(() => {
    console.log('[VariationManagement] Cancel create from sample');

    // Clear the sample data
    setSelectedSampleForNewVariation(null);
    setIsCreatingFromSample(false);

    // Generate a new UUID for the next new variation
    newVariationIdRef.current = uuidv4();
  }, []);

  // ✅ NEW: Utility function using the new utilities slice
  const uploadAudioForVariation = useCallback(async (audioBlob, variationId, metadata) => {
    try {
      console.log('VariationManagement: uploadAudioForVariation called with utilities slice');
      console.log('- audioBlob:', audioBlob);
      console.log('- variationId:', variationId);
      console.log('- metadata:', metadata);

      // Check if variationId is undefined or null
      if (!variationId) {
        console.error('Error: variationId is undefined or null');
        setUploadStatus({ loading: false, error: 'Variation ID is missing. Cannot upload audio.', success: false });
        throw new Error('Variation ID is missing. Cannot upload audio.');
      }

      setUploadStatus({ loading: true, error: null, success: false });

      // Prepare complete metadata
      const completeMetadata = {
        litGroupId: selectedLitGroup?.uuid,
        bookId: metadata.bookId || selectedBook?.uuid,
        characterId: metadata.characterId || selectedCharacter?.uuid,
        variationName: metadata.variationName,
        fileDuration: metadata.fileDuration,
        characterName: selectedCharacter?.name
      };

      // Upload both audio and text using the utilities slice
      const result = await dispatch(uploadCompleteVersion({
        audioBlob,
        textContent: metadata.content,
        variationId,
        metadata: completeMetadata
      }));

      if (result.type.endsWith('/fulfilled')) {
        console.log('Upload completed successfully via utilities slice:', result.payload);

        // Set success status
        setUploadStatus({ loading: false, error: null, success: true });

        // Return the voice S3 URL for backward compatibility
        return result.payload.voice?.voiceS3Url;
      } else {
        throw new Error(result.payload || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading audio via utilities slice:', error);
      setUploadStatus({ loading: false, error: error.message, success: false });
      throw error;
    }
  }, [setUploadStatus, dispatch, selectedLitGroup?.uuid, selectedBook?.uuid, selectedCharacter?.uuid, selectedCharacter?.name]);

  // Function to batch state updates after audio upload
  const batchedAudioPlayerUpdates = useCallback((url, s3Url) => {
    // Update the signed URL
    updateSignedUrl(url);

    // Update the form with the new s3Url
    if (formikRef.current) {
      formikRef.current.setFieldValue('s3Url', s3Url);

      // Reset the player completely if the resetPlayer function is available
      if (formikRef.current.resetPlayer && typeof formikRef.current.resetPlayer === 'function') {
        formikRef.current.resetPlayer();
      }
    }

    // Clear any pending recording
    setPendingRecording(null);
  }, [updateSignedUrl, setPendingRecording]);

  // Handle save variation
  const handleSaveVariation = useCallback(async (values, inlineRecording) => {
    console.log('VariationManagement: handleSaveVariation called with values:', values);
    console.log('VariationManagement: inlineRecording:', inlineRecording);

    try {
      let result;
      let variationId;
      let newVariation;

      // Make sure we're not submitting a placeholder s3Url
      const submissionValues = { ...values };
      if (submissionValues.s3Url === 'Recording saved, will be uploaded when variation is created' ||
        submissionValues.s3Url === 'Recording saved, will be uploaded when variation is updated' ||
        submissionValues.s3Url === 'pending-recording') {
        submissionValues.s3Url = ''; // Reset to empty string instead of placeholder text
      }

      // Get the recording data (either from inline recording or pending recording)
      const recordingToUpload = inlineRecording;

      // Check if we have a recording with duration information
      if (recordingToUpload?.metadata?.fileDuration) {
        // Include the fileDuration in the submission values
        submissionValues.fileDuration = recordingToUpload.metadata.fileDuration;
        console.log('Including fileDuration in submission:', recordingToUpload.metadata.fileDuration);
      }

      // Step 1: Create/update the variation in the database
      if (isEditing && selectedVariation) {
        // Update existing variation
        result = await updateVariation({
          variationId: selectedVariation.uuid,
          characterId: selectedCharacter.uuid,
          data: submissionValues
        });

        variationId = selectedVariation.uuid;
        newVariation = result;

        // Refresh the variation list for updates too with force=true to bypass cache
        if (characterId) {
          fetchVariations({
            litGroupId: sharedData.selections?.litGroupId,
            bookId: sharedData.selections?.bookId,
            characterId,
            forceRefresh: true
          });
        }

        // Ensure the current variation remains selected after update
        if (selectedVariation && selectedVariation.uuid) {
          // Re-select the current variation to maintain selection state
          setTimeout(() => {
            setSelection('variationId', selectedVariation.uuid);
          }, 100);
        }
      } else {
        // Check if we have a pending recording with a generated UUID
        const generatedUuid = recordingToUpload?.metadata?.variationId;

        // If we have a generated UUID from the recording, use it
        if (generatedUuid && typeof generatedUuid === 'string' &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(generatedUuid)) {
          // Include the UUID in the variation data
          submissionValues.uuid = generatedUuid;
        } else {
          // Otherwise, use the newVariationId
          submissionValues.uuid = newVariationIdRef.current;
        }

        // Create new variation
        result = await createVariation({
          characterId: selectedCharacter.uuid,
          data: submissionValues
        });

        // Check if result has the expected structure
        if (result && result.variation && result.variation.uuid) {
          // The result has the expected structure with a nested variation object
          variationId = result.variation.uuid;
          newVariation = result.variation;
        } else if (result && result.uuid) {
          // The result is the variation object directly
          variationId = result.uuid;
          newVariation = result;
        } else {
          // For now, we'll use the UUID we generated
          variationId = submissionValues.uuid;
          newVariation = {
            ...submissionValues,
            uuid: variationId
          };
        }

        // Generate a new UUID for the next new variation
        newVariationIdRef.current = uuidv4();

        // Refresh the variation list with force=true to bypass cache
        if (characterId) {
          fetchVariations({
            litGroupId: sharedData.selections?.litGroupId,
            bookId: sharedData.selections?.bookId,
            characterId,
            forceRefresh: true
          });
        }
      }

      // Step 2: If we have a recording, upload it using the valid variationId
      if (recordingToUpload && recordingToUpload.audioBlob && recordingToUpload.metadata) {
        try {
          // Double check that we have a valid variationId before proceeding
          if (!variationId) {
            throw new Error('Variation ID is missing. Cannot upload audio.');
          }

          // Validate the variationId is a valid UUID
          const isValidUuid = variationId && typeof variationId === 'string' &&
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(variationId);

          if (!isValidUuid) {
            throw new Error('Invalid variation ID format. Cannot upload audio.');
          }

          // Update metadata with the correct variationId
          const completeMetadata = {
            ...recordingToUpload.metadata,
            bookId: recordingToUpload.metadata.bookId || selectedBook?.uuid,
            characterId: recordingToUpload.metadata.characterId || selectedCharacter?.uuid,
            variationId: variationId, // Use the validated variationId
            // Use the actual variation name from the form values
            variationName: values.name || recordingToUpload.metadata.variationName || 'Unnamed Variation',
            content: values.content // get content from form's Content Text* field
          };

          // Step 3: Upload the audio
          const s3Url = await uploadAudioForVariation(
            recordingToUpload.audioBlob,
            variationId,
            completeMetadata
          );

          // Step 4: Update the variation with the S3 URL
          if (s3Url) {
            // Get the current version from the variation object
            const currentVersion = newVariation.version || 1;

            // Create a new values object with the s3Url and the updated version
            const updatedValues = {
              ...submissionValues,
              s3Url,
              version: currentVersion + 1 // Increment the version
            };

            // Update the variation in the database
            await updateVariation({
              variationId: variationId,
              characterId: selectedCharacter.uuid,
              data: updatedValues
            });

            // Update the signed URL for the audio player
            try {
              const url = await getSignedUrlForS3Path(s3Url, userId);
              setTimeout(() => batchedAudioPlayerUpdates(url, s3Url), 50);
            } catch (err) {
              console.error('Error getting signed URL for uploaded recording:', err);
              updateSignedUrl('');
            }
          }
        } catch (uploadError) {
          console.error('Error uploading audio after variation creation:', uploadError);
          // We don't want to fail the whole operation if just the audio upload fails
          // The variation has been created/updated successfully
        }
      }

      setSuccessMessage('Variation saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);

      return true;
    } catch (error) {
      console.error('Error submitting variation:', error);
      setErrorMessage('Error saving variation: ' + error.message);
      setTimeout(() => setErrorMessage(''), 5000);
      return false;
    }
  }, [
    isEditing,
    selectedVariation,
    selectedCharacter,
    selectedBook,
    sharedData.selections?.litGroupId, sharedData.selections?.bookId,
    // selectedLitGroup,
    updateVariation,
    createVariation,
    characterId,
    fetchVariations,
    uploadAudioForVariation,
    userId,
    updateSignedUrl,
    batchedAudioPlayerUpdates,
    setSelection
  ]);

  return (
    <ManagementHeader
      title="Variation Management"
      error={errorMessage}
      isLoading={false}
      width="1024px"
      isEditing={isEditing}
    >
      <Grid>
        <Grid.Row>
          <Grid.Column width={6}>
            <div>
              <div style={{ marginBottom: '1em' }}>
                <div style={{
                  marginTop: '0.6em',
                  position: 'relative',
                  zIndex: 1003
                }}>
                  <label style={{ display: 'block', marginBottom: '0.5em' }}>Select Literary Group</label>
                  <Dropdown
                    selection
                    options={litGroupOptions}
                    value={litGroupId}
                    onChange={(e, { value }) => handleLitGroupSelect(value)}
                    placeholder="Select Literary Group"
                    fluid
                    className="variation-dropdown-lit-group"
                    disabled={litGroupOptions.length === 0}
                  />
                </div>
              </div>

              {litGroupId && (
                <div style={{ marginBottom: '1em' }}>
                  <div style={{
                    marginTop: '0.6em',
                    position: 'relative',
                    zIndex: 1002
                  }}>
                    <label style={{ display: 'block', marginBottom: '0.5em' }}>Select Book</label>
                    <Dropdown
                      selection
                      options={bookOptions}
                      value={bookId}
                      onChange={(e, { value }) => handleBookSelect(value)}
                      placeholder="Select Book"
                      fluid
                      className="variation-dropdown-book"
                      disabled={bookOptions.length === 0}
                    />
                    {bookOptions.length === 0 && litGroupId && (
                      <Message info size="tiny" style={{ marginTop: '0.5em' }}>
                        <p>No books found for this literary group. Please create a book first.</p>
                      </Message>
                    )}
                  </div>
                </div>
              )}

              {bookId && (
                <div style={{ marginBottom: '1em' }}>
                  <div style={{
                    marginTop: '0.6em',
                    position: 'relative',
                    zIndex: 1001
                  }}>
                    <label style={{ display: 'block', marginBottom: '0.5em' }}>Select Character</label>
                    <Dropdown
                      selection
                      options={characterOptions}
                      value={characterId}
                      onChange={(e, { value }) => handleCharacterSelect(value)}
                      placeholder="Select Character"
                      fluid
                      className="variation-dropdown-character"
                      disabled={characterOptions.length === 0}
                    />
                    {characterOptions.length === 0 && bookId && (
                      <Message info size="tiny" style={{ marginTop: '0.5em' }}>
                        <p>No characters found for this book. Please create a character first.</p>
                      </Message>
                    )}
                  </div>
                </div>
              )}

              {selectedCharacter && (
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <Header as="h3" style={{ margin: 0 }}>Variations</Header>
                    <Button
                      primary
                      size="small"
                      icon
                      labelPosition="left"
                      onClick={handleNewVariationClick}
                      style={{ marginRight: '10px' }}
                    >
                      <Icon name="add" />
                      New Variation
                    </Button>
                  </div>

                  <VariationsListEnh
                    onVariationSelect={handleVariationSelect}
                    onDeleteVariation={handleDeleteVariation}
                    onVariationListRefreshRef={variationListRefreshRef}
                  />
                </div>
              )}
            </div>
          </Grid.Column>

          <Grid.Column width={10}>
            {selectedCharacter && (
              <>
                {successMessage && (
                  <Message positive>
                    <Message.Header>Success</Message.Header>
                    <p>{successMessage}</p>
                  </Message>
                )}
                <VariationFormPanel
                  // Form props
                  isEditing={isEditing}
                  formikRef={formikRef}
                  handleSubmit={handleSaveVariation}
                  // Entity props
                  selectedBook={selectedBook}
                  selectedCharacter={selectedCharacter}
                  selectedVariation={selectedVariation}
                  // Audio props
                  signedAudioUrl={signedAudioUrl}
                  activeParams={activeParams}
                  // Other props
                  newVariationId={newVariationIdRef.current}
                  setIsEditing={setIsEditing}
                  setShowDeleteModal={setShowDeleteModal}
                  onSampleDelete={() => {
                    // Trigger refresh of the variation list to update counts
                    if (variationListRefreshRef.current) {
                      variationListRefreshRef.current();
                    }
                  }}
                  // "Use as Base" functionality props
                  selectedSampleForNewVariation={selectedSampleForNewVariation}
                  isCreatingFromSample={isCreatingFromSample}
                  onUseAsBase={handleUseAsBase}
                  onCancelCreateFromSample={handleCancelCreateFromSample}
                />
              </>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModalWrapper
        open={showDeleteModal}
        variationToDelete={variationToDelete}
        onConfirm={confirmDeleteVariation}
        onCancel={() => {
          setShowDeleteModal(false);
          setVariationToDelete(null);
        }}
      />
    </ManagementHeader>
  );
};

export default VariationManagementEnh;
